"use client";

import { useState, useCallback } from "react";
import { PEEQD_HOME_CONTENT } from "@/config/peeqdContent";
import PeeqdTopbar from "./PeeqdTopbar";
import PeeqdHero from "./PeeqdHero";
import PeeqdPreview from "./PeeqdPreview";
import PeeqdActivityTrackerIntro from "./PeeqdActivityTrackerIntro";
import PeeqdProofStrip, { PeeqdHomeFeatures, PeeqdHomeWho, PeeqdHomeFacts } from "./PeeqdSections";
import { PeeqdFAQ, PeeqdHomeCTA, PeeqdFooter } from "./PeeqdMarketing";
import PeeqdToolDashboardZone from "@/components/peeqd/tracking/PeeqdToolDashboardZone";
import DashboardBootstrapShell from "@/components/peeqd/tracking/DashboardBootstrapShell";
import { useToolDashboard } from "@/hooks/useToolDashboard";
import { trackFunnelEvent } from "@/libs/ga";
import type {
  PeeqdTopbarUser,
  ToolDashboardInitialState,
} from "@/components/peeqd/tracking/types";

interface PeeqdHomePageProps {
  initialDashboard: ToolDashboardInitialState;
  initialUser: PeeqdTopbarUser | null;
}

export default function PeeqdHomePage({
  initialDashboard,
  initialUser,
}: PeeqdHomePageProps) {
  const [username, setUsername] = useState("");
  const [lookupUsername, setLookupUsername] = useState<string | null>(null);
  const [previewTrigger, setPreviewTrigger] = useState(0);
  const dashboard = useToolDashboard(initialDashboard);

  const handleCta = useCallback(() => {
    const trimmed = username.trim().replace(/^@+/, "");
    if (!trimmed) return;
    trackFunnelEvent("tool_engagement", "preview_started", {
      tool_id: "followers-following",
      username: trimmed,
      source: "home_hero",
    });
    setLookupUsername(trimmed);
    setPreviewTrigger((n) => n + 1);
    requestAnimationFrame(() => {
      document.getElementById("preview")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [username]);

  const handleShowcaseReset = useCallback(() => {
    setLookupUsername(null);
    setPreviewTrigger(0);
  }, []);

  const showLanding = dashboard.pageView === "landing" && !dashboard.isBootstrapping;

  return (
    <>
      <PeeqdTopbar
        content={PEEQD_HOME_CONTENT}
        showToolPills={false}
        initialUser={initialUser}
      />
      {dashboard.isBootstrapping && <DashboardBootstrapShell />}
      {!dashboard.isBootstrapping && dashboard.pageView === "dashboard" && (
        <PeeqdToolDashboardZone
          toolContent={PEEQD_HOME_CONTENT}
          toolId="followers-following"
          accounts={dashboard.accounts}
          selectedAccountId={dashboard.selectedAccountId}
          onSelectAccount={dashboard.selectAccount}
          account={dashboard.account}
          changelog={dashboard.changelog}
          followers={dashboard.followers}
          following={dashboard.following}
          followersTrackedCount={dashboard.followersTrackedCount}
          followingTrackedCount={dashboard.followingTrackedCount}
          isScraping={dashboard.isScraping}
          listsReady={dashboard.listsReady}
          activityReady={dashboard.activityReady}
          likesReady={dashboard.likesReady}
          isCollecting={dashboard.isCollecting}
          detailLoading={dashboard.detailLoading}
          profiles={dashboard.profiles}
          onConnectAccount={dashboard.connectAccount}
          connectLoading={dashboard.connectLoading}
          connectError={dashboard.connectError}
        />
      )}
      {showLanding && (
      <main className="px-4 sm:px-6 lg:px-10">
        <PeeqdHero
            content={PEEQD_HOME_CONTENT}
            username={username}
            onUsernameChange={setUsername}
            onCta={handleCta}
            variant="home"
          />

        <div id="preview" className="scroll-mt-8 overflow-visible">
          <PeeqdPreview
            username={username}
            lookupUsername={lookupUsername}
            trigger={previewTrigger}
            toolId="followers-following"
            showcase
            onShowcaseReset={handleShowcaseReset}
          />
        </div>

        <PeeqdActivityTrackerIntro />

        <>
          <PeeqdHomeFeatures />
          <PeeqdHomeWho />
          <PeeqdHomeFacts />
          <PeeqdFAQ />
          <PeeqdHomeCTA />
          <PeeqdFooter />
        </>
      </main>
      )}
    </>
  );
}
